<template>
  <div class="sensor-panel">
    <div class="sensor-list">
      <div v-if="loading" class="sensor-status">Loading sensors...</div>
      <div v-else-if="error" class="sensor-status">{{ error }}</div>
      <div v-else-if="!cards.length" class="sensor-status">No sensors</div>
      <div v-for="card in cards" :key="card.id" class="sensor-card-wrap">
        <button
          type="button"
          class="sensor-card"
          :class="{ active: String(card.id) === String(selectedId) }"
          @click="selectDevice(card.id)"
        >
          <span
            class="device-dot"
            :class="card.connected ? 'is-connected' : 'is-offline'"
            :title="card.connected ? 'Connected' : 'Not connected'"
          ></span>
          <span class="sensor-card-body">
            <span class="sensor-name">{{ card.name }}</span>
            <span class="sensor-seen">{{ card.seen }}</span>
            <span class="sensor-probes">
              <span
                v-for="item in cardProbeLine(card)"
                :key="item.key"
                class="probe"
              >
                {{ item.name }}
                <strong>{{ formatProbeTemp(item.temp) }}</strong>
              </span>
            </span>
          </span>
        </button>
        <button
          v-if="String(card.id) === String(selectedId)"
          type="button"
          class="sensor-gear"
          title="Device settings"
          aria-label="Device settings"
          @click="openDeviceSettings"
        >
          <i class="fas fa-cog"></i>
        </button>
      </div>
    </div>

    <section class="sensor-chart-dock" :aria-label="chartRangeLabel">
      <div class="chart-title">
        <span>{{ selectedId != null ? selectedName : '' }}</span>
        <button
          v-if="selectedId != null"
          type="button"
          class="chart-range"
          @click="hourMenuOpen = true"
        >
          {{ chartRangeLabel }}
        </button>
      </div>
      <div class="chart-frame">
        <div
          v-if="selectedId != null && chartLoading && spark.empty"
          class="sensor-status"
        >
          Loading chart...
        </div>
        <div
          v-else-if="selectedId != null && chartError && spark.empty"
          class="sensor-status"
        >
          {{ chartError }}
        </div>
        <div v-else-if="selectedId != null && spark.empty" class="sensor-status">
          No readings in the {{ chartRangeLabel }}
        </div>
        <svg
          v-else-if="selectedId != null"
          class="sensor-chart"
          viewBox="0 0 320 120"
          role="img"
          :aria-label="`${selectedName} ${chartRangeLabel}`"
        >
          <g v-for="tick in spark.grid" :key="tick.label">
            <line
              class="grid-line"
              :x1="tick.x1"
              :x2="tick.x2"
              :y1="tick.y"
              :y2="tick.y"
            />
            <text class="y-label" :x="tick.x1 - 4" :y="tick.y">
              {{ tick.label }}
            </text>
          </g>
          <path class="line line-1" :d="spark.sensor1" />
          <path class="line line-2" :d="spark.sensor2" />
        </svg>
      </div>
      <div class="chart-legend">
        <span class="probe probe-1">{{ selectedSensorName(1) }}</span>
        <span class="probe probe-2">{{ selectedSensorName(2) }}</span>
        <span class="chart-axis">{{ chartHours }}h ago</span>
        <span class="chart-axis chart-axis-end">now</span>
      </div>
    </section>
    <div
      v-if="hourMenuOpen"
      class="hour-menu-backdrop"
      @click.self="hourMenuOpen = false"
    >
      <div class="hour-menu" role="dialog" aria-label="Chart range">
        <div class="hour-menu-bar">
          <span>Hours</span>
          <button
            type="button"
            class="hour-menu-close"
            aria-label="Close"
            @click="hourMenuOpen = false"
          >
            ×
          </button>
        </div>
        <button
          v-for="hours in CHART_HOUR_CHOICES"
          :key="hours"
          type="button"
          class="hour-choice"
          :class="{ active: hours === chartHours }"
          @click="chooseChartHours(hours)"
        >
          {{ hours }} {{ hours === 1 ? 'hour' : 'hours' }}
        </button>
      </div>
    </div>
    <DeviceSettingsDialog
      v-if="settingsOpen && selectedId != null"
      :key="selectedId"
      :device-id="selectedId"
      @close="settingsOpen = false"
      @saved="applySavedDeviceSettings"
      @erased="onDeviceReadingsErased"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import axios from '@/axiosConfig';
import DeviceSettingsDialog from '@/components/DeviceSettingsDialog.vue';
import { sensorDisplayName } from '@/utils/tempChartDevices';
import {
  buildSparkline,
  CHART_HOUR_CHOICES,
  chartReadingLimit,
  chartRefreshMs,
  chartSinceParam,
  chartWindowSince,
  formatProbeTemp,
  latestProbeTemp,
  probeLineWithOutside,
  SELECTED_SENSOR_KEY,
  rememberedSensorId,
  sensorSeenLine,
} from '@/utils/propertySensors';

const devices = ref([]);
const latest = ref([]);
const loading = ref(true);
const error = ref('');
const selectedId = ref(null);
const chartReadings = ref([]);
const chartSince = ref(null);
const chartUntil = ref(null);
const chartLoading = ref(false);
const chartError = ref('');
const chartHours = ref(8);
const outsideTemp = ref(null);
const hourMenuOpen = ref(false);
const settingsOpen = ref(false);
let pollTimer = null;
let chartPollTimer = null;
let chartPollMs = 0;
let chartToken = 0;

const chartRangeLabel = computed(() =>
  chartHours.value === 1 ? 'last 1 hour' : `last ${chartHours.value} hours`,
);

const cards = computed(() =>
  (devices.value || []).map((device) => ({
    id: device.id,
    name: device.device_name || device.device_id,
    connected: !!device.connected,
    seen: sensorSeenLine(device.last_seen, device.sample_interval_sec),
    sensor1: latestProbeTemp(device, latest.value, 1),
    sensor2: latestProbeTemp(device, latest.value, 2),
    sensor1Name: sensorDisplayName(device.sensor1_name, 1),
    sensor2Name: sensorDisplayName(device.sensor2_name, 2),
  })),
);

function cardProbeLine(card) {
  return probeLineWithOutside(
    [
      { key: 's1', name: card.sensor1Name, temp: card.sensor1 },
      { key: 's2', name: card.sensor2Name, temp: card.sensor2 },
    ],
    outsideTemp.value,
  );
}

function selectedSensorName(which) {
  const card = cards.value.find(
    (item) => String(item.id) === String(selectedId.value),
  );
  if (!card) return sensorDisplayName('', which);
  return which === 2 ? card.sensor2Name : card.sensor1Name;
}

const selectedName = computed(() => {
  const card = cards.value.find(
    (item) => String(item.id) === String(selectedId.value),
  );
  return card ? card.name : 'Sensor';
});

const spark = computed(() =>
  buildSparkline(
    chartReadings.value,
    320,
    120,
    8,
    chartSince.value,
    chartUntil.value,
    36,
  ),
);

async function loadOutside() {
  try {
    const resp = await axios.get('/api/sensors/outside');
    const temp = resp.data && resp.data.tempF;
    outsideTemp.value =
      temp == null || temp === '' || !Number.isFinite(Number(temp))
        ? null
        : Number(temp);
  } catch {
    // Keep the last outside temperature when the weather call fails.
  }
}

async function load() {
  error.value = '';
  try {
    const [deviceResp, latestResp] = await Promise.all([
      axios.get('/api/sensors/devices'),
      axios.get('/api/sensors/readings/latest'),
    ]);
    devices.value = Array.isArray(deviceResp.data) ? deviceResp.data : [];
    latest.value = Array.isArray(latestResp.data) ? latestResp.data : [];
  } catch {
    error.value = 'Could not load sensors';
    devices.value = [];
    latest.value = [];
  } finally {
    loading.value = false;
    scheduleChartPoll();
  }
  nextTick(() => {
    loadOutside();
  });
}

function scheduleChartPoll() {
  if (selectedId.value == null) {
    clearInterval(chartPollTimer);
    chartPollTimer = null;
    chartPollMs = 0;
    return;
  }
  const ms = chartRefreshMs(selectedIntervalSec());
  if (chartPollTimer && chartPollMs === ms) return;
  clearInterval(chartPollTimer);
  chartPollMs = ms;
  chartPollTimer = setInterval(() => {
    if (selectedId.value != null) loadChart(selectedId.value);
  }, ms);
}

function selectedIntervalSec() {
  const device = (devices.value || []).find(
    (item) => String(item.id) === String(selectedId.value),
  );
  return device ? device.sample_interval_sec : null;
}

async function loadChart(id) {
  const token = ++chartToken;
  const until = new Date();
  const sinceDate = chartWindowSince(until, chartHours.value);
  const since = chartSinceParam(sinceDate);
  const limit = chartReadingLimit(chartHours.value, selectedIntervalSec());
  chartLoading.value = true;
  chartError.value = '';
  try {
    const resp = await axios.get('/api/sensors/readings', {
      params: { pico_id: id, since, limit, order: 'asc' },
    });
    if (token !== chartToken) return;
    chartSince.value = sinceDate.toISOString();
    chartUntil.value = until.toISOString();
    chartReadings.value = resp.data.readings || [];
  } catch {
    if (token !== chartToken) return;
    chartReadings.value = [];
    chartError.value = 'Could not load the chart';
  } finally {
    if (token === chartToken) chartLoading.value = false;
  }
}

function chooseChartHours(hours) {
  chartHours.value = hours;
  hourMenuOpen.value = false;
  if (selectedId.value != null) loadChart(selectedId.value);
}

function openDeviceSettings() {
  if (selectedId.value == null) return;
  hourMenuOpen.value = false;
  settingsOpen.value = true;
}

function applySavedDeviceSettings(row) {
  const device = (devices.value || []).find(
    (item) => String(item.id) === String(selectedId.value),
  );
  if (device && row) {
    device.device_name = row.device_name;
    device.sample_interval_sec = row.sample_interval_sec;
    device.sensor1_name = row.sensor1_name;
    device.sensor2_name = row.sensor2_name;
  }
  scheduleChartPoll();
}

function onDeviceReadingsErased() {
  if (selectedId.value != null) loadChart(selectedId.value);
}

function selectDevice(id) {
  settingsOpen.value = false;
  if (String(selectedId.value) === String(id)) {
    selectedId.value = null;
    try {
      localStorage.removeItem(SELECTED_SENSOR_KEY);
    } catch {
      // Keep the cleared selection when storage is unavailable.
    }
    chartReadings.value = [];
    chartToken += 1;
    chartLoading.value = false;
    scheduleChartPoll();
    return;
  }
  selectedId.value = id;
  try {
    localStorage.setItem(SELECTED_SENSOR_KEY, String(id));
  } catch {
    // The chart still follows this click when storage is unavailable.
  }
  loadChart(id);
  scheduleChartPoll();
}

function restoreSelectedSensor() {
  let storedId = null;
  try {
    storedId = localStorage.getItem(SELECTED_SENSOR_KEY);
  } catch {
    storedId = null;
  }
  const nextId = rememberedSensorId(
    devices.value,
    storedId,
    selectedId.value,
  );
  if (nextId == null || String(nextId) === String(selectedId.value)) return;
  selectedId.value = nextId;
  loadChart(nextId);
  scheduleChartPoll();
}

onMounted(async () => {
  await load();
  restoreSelectedSensor();
  pollTimer = setInterval(load, 15000);
});

onUnmounted(() => {
  clearInterval(pollTimer);
  clearInterval(chartPollTimer);
});

defineExpose({ reload: load });
</script>

<style scoped>
.sensor-panel {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f3f3f3;
}

.sensor-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px;
  scrollbar-width: none;
}

.sensor-list::-webkit-scrollbar {
  display: none;
}

.sensor-status {
  color: #777;
  font-size: 0.85em;
  padding: 8px;
}

.sensor-card-wrap {
  position: relative;
  margin-bottom: 8px;
}

.sensor-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: #fafafa;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  color: inherit;
  font: inherit;
}

.sensor-card.active {
  background: #e0f7fa;
  border-color: #26c6da;
  padding-right: 36px;
}

.sensor-gear {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #00838f;
  cursor: pointer;
  padding: 0;
}

.sensor-gear:hover {
  background: rgba(38, 198, 218, 0.25);
}

.device-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 4px;
  flex: 0 0 10px;
}

.device-dot.is-connected {
  background: #388e3c;
}

.device-dot.is-offline {
  background: #c62828;
}

.sensor-card-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.sensor-name {
  font-size: 0.92em;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sensor-seen {
  font-size: 0.75em;
  color: #777;
}

.sensor-probes {
  display: flex;
  gap: 12px;
  margin-top: 4px;
  font-size: 0.8em;
  color: #333;
}

.probe {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.probe strong {
  font-variant-numeric: tabular-nums;
}

.probe-1 {
  color: #d32f2f;
}

.probe-2 {
  color: #1565c0;
}

.sensor-chart-dock {
  flex: 0 0 200px;
  height: 200px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e0e0e0;
  background: #fff;
  padding: 8px 10px 12px;
}

.chart-title {
  flex: 0 0 20px;
  height: 20px;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.85em;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.chart-frame {
  flex: 0 0 140px;
  height: 140px;
  overflow: hidden;
}

.chart-range,
.chart-axis {
  font-weight: 400;
  color: #777;
  font-size: 0.75em;
}

.chart-range {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font: inherit;
}

.hour-menu-backdrop {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: flex-end;
}

.hour-menu {
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  border-top: 1px solid #e0e0e0;
  padding: 8px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hour-menu-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #333;
}

.hour-menu-close {
  border: 0;
  background: transparent;
  color: #333;
  font-size: 1.3em;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.hour-choice {
  border: 1px solid #e0e0e0;
  background: #fafafa;
  border-radius: 6px;
  padding: 8px;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: #333;
}

.hour-choice.active {
  background: #e0f7fa;
  border-color: #26c6da;
}

.sensor-chart {
  width: 100%;
  height: 140px;
  display: block;
}

.line {
  fill: none;
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.line-1 {
  stroke: #d32f2f;
}

.line-2 {
  stroke: #1565c0;
}

.grid-line {
  stroke: #e0e0e0;
  stroke-width: 1;
}

.y-label {
  fill: #666;
  font-size: 11px;
  text-anchor: end;
  dominant-baseline: middle;
}

.chart-legend {
  flex: 0 0 18px;
  height: 18px;
  overflow: hidden;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  align-items: center;
  font-size: 0.75em;
}

.chart-axis-end {
  margin-left: auto;
}
</style>
